import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Clock } from 'lucide-react';
import { type ComponentProps, useEffect, useRef, useState } from 'react';

import { cn } from '@/design-system/lib/cn';

import { isValidTime, maskTime } from './dates';

type InputAttrs = Omit<ComponentProps<'input'>, 'value' | 'defaultValue' | 'onChange' | 'type' | 'inputMode'>;

export type TimeFieldProps = InputAttrs & {
  /** `hh:mm` ou vazio. */
  value: string;
  /** Recebe `hh:mm` quando o horário está completo e válido; vazio quando não está. */
  onChange: (time: string) => void;
  /** Passo dos minutos na lista (o digitado pode ser qualquer um). */
  minuteStep?: number;
  invalid?: boolean;
};

const pad = (n: number) => String(n).padStart(2, '0');
const HOURS = Array.from({ length: 24 }, (_, h) => pad(h));

/**
 * Campo de horário: digita-se `hh:mm` com o teclado numérico ou escolhe-se a hora e os
 * minutos em duas colunas, num popover — no lugar do `<input type="time">`, que no celular
 * abre a roleta do sistema. Escolher os minutos fecha a lista.
 */
export function TimeField({
  value,
  onChange,
  minuteStep = 5,
  invalid = false,
  disabled,
  className,
  placeholder = 'hh:mm',
  onBlur,
  ...props
}: TimeFieldProps) {
  const [text, setText] = useState(value);
  const [emitted, setEmitted] = useState(value);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  if (value !== emitted) {
    setEmitted(value);
    setText(value);
  }

  const emit = (time: string) => {
    setEmitted(time);
    onChange(time);
  };

  const [hour, minute] = isValidTime(value) ? value.split(':') : [null, null];
  const minutes = [
    ...new Set([
      ...Array.from({ length: 60 / minuteStep }, (_, i) => pad(i * minuteStep)),
      ...(minute ? [minute] : []),
    ]),
  ].sort();

  // Abre com a hora e os minutos marcados à vista.
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() =>
      listRef.current
        ?.querySelectorAll<HTMLElement>('[aria-pressed="true"]')
        .forEach((el) => el.scrollIntoView({ block: 'center' }))
    );

    return () => cancelAnimationFrame(id);
  }, [open]);

  const choose = (h: string, m: string, close: boolean) => {
    const next = `${h}:${m}`;
    setText(next);
    emit(next);
    if (close) {
      setOpen(false);
      inputRef.current?.focus();
    }
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen} modal>
      <PopoverPrimitive.Anchor asChild>
        <div className={cn('relative w-full min-w-0', className)}>
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            data-slot="input"
            maxLength={5}
            placeholder={placeholder}
            disabled={disabled}
            value={text}
            aria-invalid={invalid || undefined}
            onChange={(e) => {
              const next = maskTime(e.target.value);
              setText(next);
              emit(isValidTime(next) ? next : '');
            }}
            onBlur={(e) => {
              if (text && !isValidTime(text)) setText(value);
              onBlur?.(e);
            }}
            className={cn(
              'h-10 w-full min-w-0 rounded-control border bg-white pr-11 pl-3 text-control text-ink max-md:text-small',
              'placeholder:text-ink-placeholder disabled:bg-surface-subtle disabled:text-ink-subtle',
              invalid ? 'border-status-conflict' : 'border-line-input',
              open && 'border-primary-focus ring-3 ring-primary-focus/15'
            )}
            {...props}
          />
          <PopoverPrimitive.Trigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label="Escolher horário"
              className="absolute top-1/2 right-1 grid size-8 -translate-y-1/2 place-items-center rounded-control-sm text-ink-subtle hover:bg-surface-muted hover:text-primary disabled:pointer-events-none disabled:opacity-50"
            >
              <Clock aria-hidden className="size-4.5 stroke-[1.8]" />
            </button>
          </PopoverPrimitive.Trigger>
        </div>
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          collisionPadding={8}
          aria-label="Horário"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            listRef.current?.querySelector<HTMLElement>('[aria-pressed="true"], button')?.focus();
          }}
          className={cn(
            'z-[90] w-52 rounded-block border border-line-input bg-white p-2 shadow-popover outline-none',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95'
          )}
        >
          <div ref={listRef} className="grid grid-cols-2 gap-2">
            <Column
              label="Hora"
              unit="horas"
              items={HOURS}
              current={hour}
              onPick={(h) => choose(h, minute ?? '00', false)}
            />
            <Column
              label="Minutos"
              unit="minutos"
              items={minutes}
              current={minute}
              onPick={(m) => choose(hour ?? '00', m, true)}
            />
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

/** Uma coluna da lista (horas ou minutos), com o item marcado em destaque. */
function Column({
  label,
  unit,
  items,
  current,
  onPick,
}: {
  label: string;
  unit: string;
  items: readonly string[];
  current: string | null | undefined;
  onPick: (item: string) => void;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="px-1 text-micro font-bold tracking-[0.05em] text-ink-subtle uppercase">{label}</span>
      <div
        role="group"
        aria-label={label}
        className="flex max-h-56 flex-col gap-0.5 overflow-y-auto overscroll-contain pr-0.5"
      >
        {items.map((item) => (
          <button
            key={item}
            type="button"
            aria-pressed={item === current}
            aria-label={`${item} ${unit}`}
            onClick={() => onPick(item)}
            className={cn(
              'grid h-9 w-full shrink-0 place-items-center rounded-control-sm text-small font-medium transition-colors',
              item === current ? 'bg-primary font-bold text-white' : 'text-ink hover:bg-surface-muted'
            )}
          >
            <span data-numeric aria-hidden>
              {item}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
